import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_APPROVERS, ASSIGN_INVOICE_APPROVER } from '../services/graphql';

const toBooleanLike = (value) => {
    if (value === true) return true;
    if (typeof value === 'number') return value > 0;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') return true;
        const numeric = Number(normalized);
        return Number.isFinite(numeric) && numeric > 0;
    }
    return false;
};

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const useAssignApprover = ({ enabled = true, excludeEmails = [] } = {}) => {
    const [assigningInvoiceID, setAssigningInvoiceID] = useState(null);
    const [selectedApproverEmail, setSelectedApproverEmail] = useState('');

    const [assignInvoiceApproverMutation, { loading: assignApproverLoading }] = useMutation(ASSIGN_INVOICE_APPROVER);
    const { data: approversData, loading: approversLoading } = useQuery(GET_APPROVERS, {
        skip: !enabled,
        fetchPolicy: 'network-only',
    });

    const excludedEmailSet = new Set((Array.isArray(excludeEmails) ? excludeEmails : [excludeEmails])
        .map(normalizeEmail)
        .filter(Boolean));

    const availableApprovers = (approversData?.getApprovers?.body || []).filter((approver) => {
        const approverEmail = normalizeEmail(approver?.approverEmail);
        return !toBooleanLike(approver?.outOfOffice) && !excludedEmailSet.has(approverEmail);
    });

    const openAssignApproverModal = (invoiceID) => {
        if (!enabled || !invoiceID) return;
        setAssigningInvoiceID(String(invoiceID));
        setSelectedApproverEmail('');
    };

    const closeAssignApproverModal = () => {
        setAssigningInvoiceID(null);
        setSelectedApproverEmail('');
    };

    const assignSelectedApprover = async (onSuccess) => {
        if (!assigningInvoiceID || !selectedApproverEmail) {
            alert('Please select an approver.');
            return null;
        }

        const invoiceID = String(assigningInvoiceID);
        const approverEmail = selectedApproverEmail;

        try {
            const response = await assignInvoiceApproverMutation({
                variables: {
                    invoiceID,
                    approverEmail,
                },
            });
            const statusCode = response?.data?.assignInvoiceApprover?.status?.statusCode;
            if (statusCode !== '200') {
                throw new Error(response?.data?.assignInvoiceApprover?.body || 'Unable to assign approver.');
            }

            closeAssignApproverModal();
            if (typeof onSuccess === 'function') {
                await onSuccess({ invoiceID, approverEmail, response });
            }
            return response;
        } catch (error) {
            alert(error?.message || 'Unable to assign approver.');
            return null;
        }
    };

    return {
        assigningInvoiceID,
        selectedApproverEmail,
        setSelectedApproverEmail,
        availableApprovers,
        assignApproverLoading,
        approversLoading,
        openAssignApproverModal,
        closeAssignApproverModal,
        assignSelectedApprover,
    };
};

export default useAssignApprover;
